import { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Stack,
    Typography,
    Button,
    CircularProgress,
    Alert,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { getReturnAvailableTripsAPI, verifyReturnTicketAPI } from "../api/tickets";
import SeatSelection from "./SeatSelection";
import AppDatePicker from "./AppDatePicker";

import { fmtTime as fmtTimeShared } from "../utils/format";

const fmtTime = (raw) => fmtTimeShared(raw, "--:--");

// Dijalog za OVERU otvorene povratne karte: izbor datuma → povratni polazak
// (samo povratna relacija sa karte, server-side) → opcionalno sedište → potvrda.
export default function OveraDialog({ open, onClose, ticket, onSuccess }) {
    const { t } = useTranslation();
    const [date, setDate] = useState("");
    const [trips, setTrips] = useState([]);
    const [loadingTrips, setLoadingTrips] = useState(false);
    const [tripsError, setTripsError] = useState(null);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [overaSeats, setOveraSeats] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // Reset pri otvaranju/zatvaranju.
    useEffect(() => {
        if (open) {
            setDate("");
            setTrips([]);
            setTripsError(null);
            setSelectedTrip(null);
            setOveraSeats([]);
            setSubmitError(null);
        }
    }, [open, ticket]);

    const depName = ticket?.departureStationName || "";
    const arrName = ticket?.arrivalStationName || "";

    const fetchTrips = useCallback(async (chosenDate) => {
        if (!ticket?.id || !chosenDate) return;
        setLoadingTrips(true);
        setTripsError(null);
        setSelectedTrip(null);
        setOveraSeats([]);
        try {
            const data = await getReturnAvailableTripsAPI(ticket.id, chosenDate);
            setTrips(Array.isArray(data) ? data : data?.content || []);
        } catch (err) {
            console.error("Greška pri dovlačenju povratnih polazaka:", err);
            setTripsError(err?.response?.data?.message || t("overa.tripsError"));
            setTrips([]);
        } finally {
            setLoadingTrips(false);
        }
    }, [ticket, t]);

    useEffect(() => {
        if (open && date) fetchTrips(date);
    }, [open, date, fetchTrips]);

    const getTripId = (tr) => tr.tripId || tr.id;
    const getFreeSeats = (tr) => tr.freeSeats ?? tr.availableSeats ?? tr.seats;

    const handleConfirm = async () => {
        if (!selectedTrip) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            await verifyReturnTicketAPI(ticket.id, {
                tripId: getTripId(selectedTrip),
                journeyDate: date,
                seat: overaSeats[0]?.seatNumber ? Number(overaSeats[0].seatNumber) : null,
            });
            onSuccess?.();
        } catch (err) {
            console.error("Greška pri overi:", err);
            setSubmitError(err?.response?.data?.message || t("overa.submitError"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
            <DialogTitle sx={{ pr: 6 }}>
                <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Icon icon="mdi:ticket-confirmation-outline" width={22} color="#1a1a2e" />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{t("overa.title")}</Typography>
                    </Stack>
                    {ticket && (
                        <Typography variant="body2" color="text.secondary">
                            {t("overa.returnRelation")}: <b>{depName} → {arrName}</b>
                        </Typography>
                    )}
                </Stack>
                <IconButton onClick={onClose} aria-label={t("common.close")} sx={{ position: "absolute", right: 8, top: 8 }}>
                    <Icon icon="mdi:close" width={22} />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2.5}>
                    {/* 1. Datum povratka */}
                    <AppDatePicker
                        label={t("overa.returnDate")}
                        value={date}
                        onChange={setDate}
                        disablePast
                    />

                    {/* 2. Izbor polaska */}
                    {date && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                {t("overa.chooseDeparture")}
                            </Typography>
                            {loadingTrips ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : tripsError ? (
                                <Alert severity="error">{tripsError}</Alert>
                            ) : trips.length === 0 ? (
                                <Alert severity="info">{t("overa.noTrips")}</Alert>
                            ) : (
                                <Stack spacing={1}>
                                    {trips.map((tr) => {
                                        const id = getTripId(tr);
                                        const isSel = selectedTrip && getTripId(selectedTrip) === id;
                                        const free = getFreeSeats(tr);
                                        return (
                                            <Box
                                                key={id}
                                                onClick={() => { setSelectedTrip(tr); setOveraSeats([]); }}
                                                sx={{
                                                    p: 1.5, borderRadius: 2, cursor: "pointer",
                                                    border: "2px solid",
                                                    borderColor: isSel ? "secondary.main" : "grey.200",
                                                    bgcolor: isSel ? "secondary.lighter" : "white",
                                                    transition: "all .15s",
                                                    "&:hover": { borderColor: "secondary.light" },
                                                }}
                                            >
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                                        <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>
                                                            {fmtTime(tr.departureTime || tr.calculatedDepartureTime)}
                                                        </Typography>
                                                        <Stack>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                {tr.lineName || tr.operatorName || ""}
                                                            </Typography>
                                                            {free != null && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {t("overa.freeSeats")}: {free}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Stack>
                                                    {isSel && <Icon icon="mdi:check-circle" width={22} color="#3366FF" />}
                                                </Stack>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>
                    )}

                    {/* 3. Sedište — opciono biranje samo ako je polazak numerisan, inače NN */}
                    {selectedTrip && (
                        selectedTrip.seatsNumbered ? (
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                    {t("overa.chooseSeat")}
                                </Typography>
                                <SeatSelection
                                    key={getTripId(selectedTrip)}
                                    trip={selectedTrip}
                                    journeyDate={date}
                                    passengers={1}
                                    selectedSeats={overaSeats}
                                    onSeatsChange={setOveraSeats}
                                />
                            </Box>
                        ) : (
                            <Alert severity="info" icon={<Icon icon="mdi:seat-outline" width={20} />}>
                                {t("overa.nonNumberedNote")}
                            </Alert>
                        )
                    )}

                    {submitError && <Alert severity="error">{submitError}</Alert>}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} sx={{ fontWeight: 600, textTransform: "none" }}>
                    {t("overa.cancel")}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={!selectedTrip || submitting}
                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Icon icon="mdi:check" />}
                    sx={{ fontWeight: 700, textTransform: "none", borderRadius: 2 }}
                >
                    {submitting ? t("overa.submitting") : t("overa.confirm")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
